/**
 * The only place the browser talks to the server.
 *
 * Cookies carry the session, so nothing here touches localStorage: a token in
 * JS-readable storage is one XSS away from a full account takeover.
 */

import type { SectionKind } from '../../shared/section-kinds.ts';

/**
 * Told when a write succeeds, so the read cache can drop what it holds.
 *
 * A hook rather than an import of cache.ts: that module reads `session()` from
 * here, and two modules importing each other is a cycle that works until a
 * bundler orders them the other way round. realtime.ts owns both and wires them
 * together at start-up.
 */
let onWrite: () => void = () => undefined;

export function setOnWrite(fn: () => void): void {
  onWrite = fn;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** Field-level reasons, when the server could safely name them. */
  readonly fields: string[];

  constructor(status: number, code: string, message: string, fields: string[] = []) {
    super(message);
    this.status = status;
    this.code = code;
    this.fields = fields;
  }
}

let csrfToken: string | null = null;

async function ensureCsrf(): Promise<string> {
  if (csrfToken) return csrfToken;
  const res = await fetch('/api/v1/auth/csrf', { credentials: 'same-origin' });
  if (!res.ok) throw new ApiError(res.status, 'csrf_unavailable', 'Could not start a session.');
  const payload = (await res.json()) as { csrfToken: string };
  csrfToken = payload.csrfToken;
  return csrfToken;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  /** Set by the retry below. A request is re-authenticated at most once. */
  retried?: boolean;
}

/**
 * Who asks the person to prove themselves again.
 *
 * Registered by StepUpDialog, which is mounted once in the shell. Returning true
 * means the re-authentication succeeded and the request should be retried; false
 * means report the refusal as it is. Null - nothing mounted yet - falls back to
 * the provider navigation below, which is what this always did.
 */
type StepUpHandler = () => Promise<boolean>;
let stepUpHandler: StepUpHandler | null = null;

export function setStepUpHandler(handler: StepUpHandler | null): void {
  stepUpHandler = handler;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers };

  if (method !== 'GET') {
    headers['X-CSRF-Token'] = await ensureCsrf();
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(`/api/v1${path}`, {
    method,
    credentials: 'same-origin',
    headers,
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
  });

  /*
   * A write invalidates what was read.
   *
   * The cache is normally invalidated by the change stream, which is correct and
   * one round trip late - fine for a screen sitting still, wrong for the screen
   * that just saved and navigated. The plan screen did exactly that: it PATCHed a
   * title and pushed to the editor, whose loader was served the pre-save row from
   * cache and rendered the old title over a saved new one.
   *
   * Dropped rather than marked stale: a marked entry still hands back the old
   * answer once, which is the bug. The write already cost a round trip, so one
   * more on the next read is not what anybody notices - and the stream still
   * handles every write made by somebody else.
   */
  if (method !== 'GET' && res.ok) {
    forgetSession();
    onWrite();
  }

  if (res.status === 204) return null as T;
  const payload = (await res.json().catch(() => ({}))) as {
    error?: { code?: string; message?: string; details?: { fields?: string[] } };
  };
  if (!res.ok) {
    // A rotated session invalidates the CSRF token too; force a refetch.
    if (res.status === 403) csrfToken = null;

    /*
     * A sensitive action the session has not proved itself for recently.
     *
     * The answer is a re-authentication, and with OIDC that is a top-level
     * navigation - the provider will not be framed and must not be. Sending the
     * browser there is the only thing the client can usefully do, and
     * `redirect_to` brings the person back to the screen they were on.
     */
    if (payload.error?.code === 'step_up_required' && !options.retried) {
      /*
       * Ask in place and retry, so the form that triggered this keeps what was
       * typed into it. Without a handler - or for an account that holds no
       * password - the dialog sends the browser to the provider instead, which
       * is a top-level navigation because a provider will not be framed.
       */
      if (stepUpHandler) {
        if (await stepUpHandler()) return api<T>(path, { ...options, retried: true });
      } else {
        const back = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.assign(`/api/v1/auth/step-up?redirect_to=${back}`);
      }
    }
    throw new ApiError(
      res.status,
      payload.error?.code ?? 'error',
      payload.error?.message ?? 'Request failed',
      payload.error?.details?.fields ?? [],
    );
  }
  return payload as T;
}

/**
 * Where an image lives.
 *
 * The id is the address, so this URL's bytes can never change - which is what
 * lets the server serve it `immutable` for a year and the browser fetch each
 * face exactly once. Replacing a picture produces a new id, and the new id
 * arrives in the same payload the screen already loads.
 */
export const imageUrl = (id: string) => `/api/v1/images/${id}`;

/**
 * Upload one image. The body is the file; there is no second field to wrap.
 *
 * Not routed through `api()` because that sets a JSON content type and stringifies
 * the body - and here the content type is what tells the server which of the two
 * accepted formats to expect.
 */
/**
 * POST a file as the body.
 *
 * Same shape as putImage and for the same reason: the document IS the request,
 * so the content type is what tells the server which of the three accepted
 * formats to expect - and the server re-derives it from the bytes anyway.
 */
export async function postFile<T>(path: string, file: File): Promise<T> {
  const headers: Record<string, string> = {
    'content-type': file.type || 'application/octet-stream',
  };
  headers['x-csrf-token'] = await ensureCsrf();

  const res = await fetch(`/api/v1${path}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers,
    body: file,
  });
  forgetSession();
  onWrite();
  const payload = (await res.json().catch(() => ({}))) as {
    error?: { code?: string; message?: string; details?: { fields?: string[] } };
  };
  if (!res.ok) {
    throw new ApiError(
      res.status,
      payload.error?.code ?? 'error',
      payload.error?.message ?? 'Request failed',
      payload.error?.details?.fields ?? [],
    );
  }
  return payload as T;
}

export async function putImage(path: string, blob: Blob): Promise<{ id: string }> {
  return sendImage('PUT', path, blob);
}

/** The same upload, as a POST - for a route that adds a picture rather than replacing one. */
export async function postImage(path: string, blob: Blob): Promise<{ id: string; url: string }> {
  return sendImage('POST', path, blob) as Promise<{ id: string; url: string }>;
}

async function sendImage(method: 'PUT' | 'POST', path: string, blob: Blob): Promise<{ id: string }> {
  const res = await fetch(`/api/v1${path}`, {
    method,
    credentials: 'same-origin',
    headers: { 'Content-Type': blob.type, 'X-CSRF-Token': await ensureCsrf() },
    body: blob,
  });
  if (!res.ok) {
    if (res.status === 403) csrfToken = null;
    const payload = (await res.json().catch(() => ({}))) as {
      error?: { code?: string; message?: string };
    };
    throw new ApiError(
      res.status,
      payload.error?.code ?? 'error',
      payload.error?.message ?? '画像を保存できませんでした',
    );
  }
  return (await res.json()) as { id: string };
}

export const get = <T>(path: string) => api<T>(path);
export const post = <T>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body });
export const patch = <T>(path: string, body: unknown) => api<T>(path, { method: 'PATCH', body });
export const del = <T>(path: string) => api<T>(path, { method: 'DELETE' });
export const put = <T>(path: string, body: unknown) => api<T>(path, { method: 'PUT', body });

/**
 * The current session, fetched once per navigation.
 *
 * Route loaders run in parallel, so the root loader and every screen below it
 * would otherwise each ask `/auth/me` on the same navigation - and, worse, a
 * tenant screen would fire its own request before the root loader had a chance
 * to redirect a user who has no clinic at all.
 *
 * The promise is cached only while it is in flight. Loaders started together
 * share one request; the next navigation fetches again, so nothing here can
 * serve a membership or a scope that has since changed.
 */
let cached: Promise<Session> | null = null;

/** Any successful write may have changed the scope, the name or the avatar. */
function forgetSession(): void {
  cached = null;
}

export function session(force = false): Promise<Session> {
  if (force) cached = null;
  if (!cached) {
    const request = get<Session>('/auth/me');
    cached = request;
    // A FAILED lookup is not an answer to cache - drop it so the next caller
    // asks again rather than inheriting the rejection forever.
    void request.catch(() => {
      if (cached === request) cached = null;
    });
  }
  return cached;
}

// --- response shapes the screens consume ---------------------------------
export interface Session {
  user: {
    id: string;
    email: string | null;
    displayName: string;
    platformRole: 'JMC_ADMIN' | null;
    /** Feed to `imageUrl`; null when they have not set one. */
    avatarId: string | null;
    /** Whether they can sign in with a password. Never the password itself. */
    hasPassword?: boolean;
  };
  activeClinicId: string | null;
  activeStoreId: string | null;
  /** The colour this session is drawn in: store's, else clinic's, else default. */
  theme?: { brandColor: string | null };
  memberships: Array<{
    clinicId: string;
    clinicName: string;
    clinicAdmin: boolean;
    /** `admin`: this person administers the store (always true for a clinic admin). */
    stores: Array<{ id: string; name: string; admin?: boolean }>;
  }>;
}

/**
 * Everything the clinic dashboard shows.
 *
 * Counts arrive as strings: Postgres `count(*)` is bigint, and node-postgres
 * hands bigints over as text rather than silently losing precision. Every use
 * site wraps them in Number.
 */
export interface DashboardData {
  stores: Array<{
    id: string;
    name: string;
    status: string;
    total: string;
    draft: string;
    in_review: string;
    approved: string;
    published: string;
    this_month: string;
    last_article_at: string | null;
    /** This store's own allowance for the period; null before its first use. */
    articlesQuota: QuotaLine | null;
    imagesQuota: QuotaLine | null;
  }>;
  recent: Array<{
    id: string;
    title: string;
    status: string;
    created_at: string;
    updated_at: string;
    store_name: string;
  }>;
  /**
   * Approved material, per kind and per scope.
   *
   * `store_id` null is the clinic's own - shared with every store. A row per
   * store carries only that store's, and RLS means a store user never receives a
   * sibling's row at all.
   */
  readiness: Array<{
    kind: string;
    store_id: string | null;
    store_name: string | null;
    approved: string;
    total: string;
  }>;
  /** Per store: whether generation there draws on the clinic's shared material. */
  factScopes: Array<{ store_id: string; use_clinic_facts: boolean }>;
}

export interface QuotaLine {
  used: number;
  limit: number | null;
  remaining: number | null;
}

export interface UsageSummary {
  planId: string;
  /** What the plan is called. `planId` is a slug and is not shown to a clinic. */
  planName: string;
  /** The window every figure on the dashboard is counted over. */
  periodStart: string | null;
  periodEnd: string | null;
  /** The selected store's own allowance. */
  articles: QuotaLine;
  images: QuotaLine;
  /** The clinic's ceiling across all stores; `limit` null means none is set. */
  clinicArticles: QuotaLine;
  clinicImages: QuotaLine;
}

export { PLAN_LIMIT_FIELDS, type PlanLimitKey } from '../../shared/plans.ts';

export interface ListResponse<T> {
  items: T[];
  /** Matching rows in total, where the endpoint counts them. Paged lists do. */
  total?: number;
}

export interface Store {
  id: string;
  name: string;
  status: 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';
  created_at: string;
  /*
   * What the list screen needs to be worth reading: where the branch is, and
   * whether it has people, articles and approved material. The list used to
   * carry a name and a date, so every one of those questions cost a click.
   */
  prefecture?: string | null;
  city?: string | null;
  address?: string | null;
  phone?: string | null;
  hours_note?: string | null;
  site_url?: string | null;
  member_count?: string | number | null;
  article_count?: string | number | null;
  fact_count?: string | number | null;
}

/** A row in the article list. Content is fetched only when one is opened. */
export interface Article {
  id: string;
  store_id: string;
  store_name: string;
  title: string;
  status: 'DRAFT' | 'IN_REVIEW' | 'APPROVED' | 'PUBLISHED' | 'ARCHIVED';
  article_type: string;
  keywords: string[];
  /** Character-count override, or null for the prompt's own guidance. */
  target_length: number | null;
  publish_on: string | null;
  version: number;
  created_at: string;
  updated_at: string;
  /** Enough to say what is left to do; see stage.ts. Absent on a single article. */
  has_body?: boolean;
  section_count?: number;
  drafted_count?: number;
}

/**
 * One section of the outline.
 *
 * `intent` is why the section is needed - written at plan time, editable, and
 * sent to the model with the heading. Everything below it is written by the
 * later phases: the length it aims at, what it may draw on, and what state its
 * text is in.
 */
export interface ArticleSection {
  id: string;
  ordinal: number;
  level: number;
  heading: string;
  intent: string;
  target_chars: number | null;
  status: 'PLANNED' | 'DRAFTED' | 'EDITED';
  body_md: string;
  /** The shape it is written in. See shared/section-kinds.ts. */
  kind: SectionKind;
  /** The approved facts this section should build on. A focus, not a filter. */
  must_include: Array<{ kind: string; id: string }>;
}

export interface ArticleDetail extends Article {
  body_md: string;
  /** The outline, in order, with what each section is for. */
  sections: ArticleSection[];
  /** How this one article should sound. '' means the clinic's standing tone. */
  tone: string;
  /** The shape it is written in. Null when nobody chose one. */
  angle: 'compare' | 'howto' | 'avoid' | 'cost' | 'case' | null;
  /** STEP 1's reading of the keyword, as the writer approved it. */
  search_intent: { background?: string; explicit?: string; latent?: string };
  /** The same headings as a flat array. Mirrored by the API; do not write to it. */
  headings: string[];
  cover_image_id: string | null;
  persona_id: string | null;
  treatment_id: string | null;
  /** The named alternative for a comparison table/section. Null for most articles. */
  comparison_treatment_id: string | null;
  supervisor_id: string | null;
  cta_template_id: string | null;
}

/**
 * Exactly what a generation call will be given for one article.
 *
 * Surfaced in the editor so an operator can see the material before spending a
 * generation on it - and see what is missing, which is the more useful half.
 */
export interface GenerationContext {
  article: {
    id: string;
    title: string;
    articleType: string;
    keywords: string[];
    publishOn: string | null;
    /** The chosen plan: the H2 headings the body is written to, in order. */
    headings: string[];
    generationNote: string;
  };
  clinic: {
    name: string;
    siteName: string;
    siteUrl: string | null;
    defaultTone: string;
    defaultKeywords: string[];
    wordpressCategory: string | null;
    /** The operating entity, as registered. '' when unregistered. */
    corporateName: string;
    representative: string;
  };
  store: {
    id: string;
    name: string;
    /** Where the branch is and when it is open. '' when unregistered. */
    postalCode: string;
    address: string;
    accessNote: string;
    phone: string;
    hoursNote: string;
    closedNote: string;
    reservationUrl: string | null;
    mapUrl: string | null;
  };
  resolved: {
    siteUrl: string | null;
    tone: string;
    keywords: string[];
    wordpressCategory: string | null;
    /** False when this store's articles must not draw on clinic-wide facts. */
    useClinicFacts: boolean;
  };
  persona: { label?: string; concerns?: string } | null;
  treatment: { name?: string; price_jpy?: number | null; devices?: string } | null;
  /** The named alternative for a comparison table/section. Null for most articles. */
  comparisonTreatment: { name?: string; price_jpy?: number | null; devices?: string } | null;
  strengths: Record<string, Array<{ id: string; title: string; body: string }>>;
  supervisors: Array<{ id: string; name: string; title: string }>;
  cta: { name?: string; button_url?: string | null } | null;
  missing: string[];
}

/**
 * A compliance check on one article.
 *
 * `verdict` is never 適合 by design - see server/modules/compliance/rules.ts.
 * These rules can show that something looks wrong; they cannot establish that an
 * article is lawful.
 */
export interface ComplianceReport {
  /** 未設定: no rulebook has ever been released, so nothing was checked. */
  verdict: '指摘なし' | '要確認' | '根拠不足' | '未設定';
  findings: Array<{
    ruleId: string;
    title: string;
    guidance: string;
    severity: 'REVIEW' | 'UNSUPPORTED';
    reference: { law: string; article: string; effectiveFrom: string };
    excerpt: string;
    at: number;
  }>;
  checkedAt: string;
  bodyLength: number;
  /** The article version the text came from, so a stale report is visible. */
  version: number;
}

/**
 * Titles and headings to choose from.
 *
 * Both lists are already trimmed, de-duplicated and capped server-side: model
 * output is input, and the screen renders what survived that rather than
 * whatever the provider sent.
 */
/** The three angles FR-032 asks a title to be written from. */
export const TITLE_AXES = {
  doubt: '疑問解消・比較',
  relief: '失敗回避・安心',
  benefit: 'ベネフィット・結果',
} as const;

export type TitleAxis = keyof typeof TITLE_AXES;

export interface TitleProposal {
  text: string;
  axis: TitleAxis;
}

/** A heading and the one-line reason it is needed (FR-033). */
export interface HeadingProposal {
  text: string;
  reason: string;
}

/** STEP 1's reading of the keyword (FR-031). */
export interface SearchIntent {
  background: string;
  explicit: string;
  latent: string;
}

export interface ProposalResult {
  /** Null when the round asked for one list only, and left the analysis alone. */
  intent: SearchIntent | null;
  titles: TitleProposal[];
  headings: HeadingProposal[];
  provider: string;
  model: string;
  promptKey: string;
  promptVersion: number | null;
  microCost: number;
  missing: string[];
}

/**
 * A replacement for one selected passage.
 *
 * Returned, never applied: only the editor knows where the selection was.
 */
export interface RewriteResult {
  text: string;
  /** Expression rules the replacement still trips, by title. See ai/generate.ts. */
  stillFlagged?: string[];
  provider: string;
  model: string;
  promptKey: string;
  promptVersion: number | null;
  microCost: number;
}

/** What one generation produced, and what it cost. */
export interface GenerationResult {
  task: 'outline' | 'body';
  text: string;
  provider: string;
  model: string;
  promptKey: string;
  promptVersion: number | null;
  microCost: number;
  missing: string[];
  applied: boolean;
}

export interface StoreDetail {
  store: {
    id: string;
    name: string;
    status: string;
    created_at: string;
    updated_at: string;
    clinic_name: string;
    /** Where the branch is and when it is open. NOT NULL text, so '' is unset. */
    postal_code: string;
    prefecture: string;
    city: string;
    address: string;
    access_note: string;
    phone: string;
    hours_note: string;
    closed_note: string;
    reservation_url: string | null;
    map_url: string | null;
    site_url: string | null;
    default_tone: string | null;
    default_keywords: string[] | null;
    wordpress_category: string | null;
    clinic_site_url: string | null;
    clinic_default_tone: string | null;
    /** Never null: the clinic's column is NOT NULL, so this is at worst empty. */
    clinic_default_keywords: string[];
    clinic_wordpress_category: string | null;
    /** False when this store's articles use only its own approved facts. */
    use_clinic_facts: boolean;
    /** This store's theme colour; null inherits the clinic's. */
    brand_color?: string | null;
    clinic_brand_color?: string | null;
  };
  facts: Array<{ kind: string; approved: number; total: number }>;
  members: Array<{
    user_id: string;
    role: string;
    status: string;
    email: string | null;
    displayName: string;
  }>;
}

export interface MemberDetail {
  userId: string;
  email: string | null;
  displayName: string;
  lastLoginAt: string | null;
  /** False until their first sign-in; only then is the email theirs to change. */
  claimed: boolean;
  accountStatus: string;
  /** One row per grant: a clinic-wide one has store_id null. */
  grants: Array<{
    id: string;
    role: string;
    status: string;
    created_at: string;
    store_id: string | null;
    store_name: string | null;
  }>;
}

/**
 * One GRANT, with the person who holds it.
 *
 * A person holding a clinic-wide role and two store roles is three of these.
 * The user screen folds them back into people; the shape stays per-grant
 * because that is what can be changed, suspended and revoked.
 */
export interface Member {
  grantId?: string;
  userId: string;
  role: string;
  status: string;
  storeId: string | null;
  storeName?: string | null;
  grantedAt?: string | null;
  email: string | null;
  displayName: string;
  avatarId?: string | null;
  lastLoginAt?: string | null;
  accountStatus?: string;
  /** False until their first sign-in; only then is the address theirs. */
  claimed?: boolean;
}

/**
 * One row of JMC's shared starting material.
 *
 * Read live where it is a choice (a tone), and copied where it is content a
 * clinic edits (a persona). `hidden` is this clinic's own answer, not the
 * platform's - see migration 089.
 */
export interface PlatformDefaultItem {
  id: string;
  kind: 'TONE' | 'ARTICLE_TYPE' | 'PERSONA' | 'CTA';
  label: string;
  description: string;
  payload: Record<string, unknown>;
  clinic_types: string[];
  status: 'DRAFT' | 'APPROVED' | 'RETIRED';
  sort_order: number;
  hidden?: boolean;
}

export interface CatalogItem {
  /** Supervisors only: their photograph, for `imageUrl`. */
  photoId?: string | null;
  id: string;
  storeId: string | null;
  status: 'DRAFT' | 'APPROVED' | 'RETIRED';
  updatedAt: string;
  [field: string]: unknown;
}

export interface AdminStore {
  id: string;
  name: string;
  status: string;
  created_at: string;
  clinic_id: string;
  clinic_name: string;
  /** The parent's state: a live store under a suspended clinic still cannot be used. */
  clinic_status: string;
  member_count: number;
}

export interface AdminUsage {
  clinic_id: string;
  name: string;
  /**
   * The scope this row measures. NULL is the clinic's own row.
   *
   * A period has one row per scope: a store's row carries the articles and
   * images it produced, the clinic's row carries the money. Summing both
   * together per clinic is what made 店舗別 analytics clinic-level.
   */
  store_id: string | null;
  store_name: string | null;
  period_start: string;
  period_end: string;
  articles_used: number;
  images_used: number;
  ai_micro_cost_used: number;
}

/** The analytics loader's payload: the ledger, plus who exists. */
export interface UsageResponse {
  items: AdminUsage[];
  entities: AdminUsageEntity[];
}

/**
 * A clinic or store that exists, whether or not it used anything.
 *
 * The usage ledger only has rows for activity, so this is what lets the analytics
 * screen show a tenant that produced nothing - which is the row an operator is
 * usually looking for.
 */
export interface AdminUsageEntity {
  clinic_id: string;
  name: string;
  store_id: string | null;
  store_name: string | null;
}

/** One thing the review found, with the evidence for it. */
export interface ReviewFinding {
  /** Stable across re-runs, so a decision about it survives one. */
  fingerprint: string;
  /** Which half found it. A pattern hit and a corpus reading fail differently. */
  source: 'rule' | 'corpus';
  severity: '要確認' | '根拠不足';
  title: string;
  guidance: string;
  /** The article text, verified to occur in the body. */
  quote: string;
  /** Offset in the body, for the jump-to-location control. */
  at: number;
  /** The provision. Present only when a citation survived verification. */
  citation: {
    chunkId: string;
    publisher: string;
    title: string;
    headingPath: string;
    excerpt: string;
    canonicalUrl: string | null;
  } | null;
  disposition: {
    state: 'OPEN' | 'ACCEPTED' | 'DISMISSED' | 'ESCALATED';
    reason: string;
    decided_at: string;
  } | null;
}

/** The stored review, and whether it still applies. */
export interface ArticleReview {
  review: {
    id: string;
    article_version: number;
    corpus_release_id: string | null;
    verdict: '指摘なし' | '要確認' | '根拠不足';
    findings: ReviewFinding[];
    /** Citations the model produced that did not survive verification. */
    dropped_citations: number;
    micro_cost: number;
    checked_at: string;
  } | null;
  corpus: { id: string; version_no: number } | null;
  /** True when the article or the corpus moved after this review was made. */
  stale: boolean;
  staleReason: 'article' | 'corpus' | null;
  articleVersion: number;
}

/** One version of the rulebook. Released like a prompt: drafted, checked, activated. */
export interface RuleSet {
  id: string;
  version_no: number;
  status: 'DRAFT' | 'ACTIVE' | 'ROLLED_BACK';
  note: string;
  created_at: string;
  activated_at: string | null;
  created_by_name: string | null;
  activated_by_name: string | null;
  rules: string;
}

export interface Rule {
  id: string;
  slug: string;
  title: string;
  guidance: string;
  fix: string;
  severity: 'REVIEW' | 'UNSUPPORTED';
  /** Regular expression sources. An array so a reviewer can read them. */
  patterns: string[];
  law: string;
  article: string;
  effective_from: string | null;
  /** Present when the rule was mined: the provision it was proposed from. */
  source_excerpt: string;
  source_heading: string | null;
}

/** A mined proposal, with what accepting it would do. */
export interface RuleCandidate {
  id: string;
  slug: string;
  title: string;
  guidance: string;
  fix: string;
  severity: 'REVIEW' | 'UNSUPPORTED';
  patterns: string[];
  law: string;
  article: string;
  source_excerpt: string;
  source_heading: string | null;
  impact: {
    /** Counts only: articles are unreadable to a platform session by design. */
    matchedArticles: number;
    totalArticles: number;
    broad: boolean;
    breaksCases: string[];
    matchesSource: boolean;
  };
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  decision_note: string;
  created_at: string;
  decided_at: string | null;
  decided_by_name: string | null;
}

/** Labelled text a rulebook must get right, in both directions. */
export interface RuleEvalCase {
  id: string;
  text: string;
  expect_slug: string | null;
  should_flag: boolean;
  is_active: boolean;
  created_at: string;
}

export interface RuleCheckResult {
  status: 'PASSED' | 'FAILED';
  total: number;
  passed: number;
  failed: number;
  results: {
    id: string;
    text: string;
    expectSlug: string | null;
    shouldFlag: boolean;
    passed: boolean;
    /** Which rules actually fired, so a failure says what happened. */
    firedSlugs: string[];
  }[];
}

/** What the checker is running right now. Null version = the compiled-in set. */
export interface ActiveRulebook {
  setId: string | null;
  versionNo: number | null;
  rules: { slug: string; title: string; severity: string; law: string; article: string }[];
}

export interface MineResult {
  release: { id: string; versionNo: number };
  proposed: number;
  queued: number;
  rejected: number;
  chunksRead: number;
  microCost: number;
  rejections: Record<string, number>;
  sample: string;
}

/** A publication the corpus draws on: a law, a guideline, a notice. */
export interface LegalSource {
  id: string;
  publisher: string;
  title: string;
  canonical_url: string | null;
  kind: 'law' | 'guideline' | 'notice' | 'internal';
  notes: string;
  created_at: string;
  versions: string;
  last_fetched_at: string | null;
  current_document_id: string | null;
}

/** One fetch or upload of a source. Immutable; a change is a new version. */
export interface LegalDocument {
  id: string;
  version_no: number;
  origin: 'url' | 'upload';
  origin_url: string | null;
  content_type: string;
  byte_size: number;
  /** The document's identity: the digest of the bytes as published. */
  sha256: string;
  parser_version: string;
  status: 'FETCHING' | 'READY' | 'FAILED' | 'SUPERSEDED';
  published_on: string | null;
  fetched_at: string;
  created_at: string;
  text_length: number;
}

export interface LegalFetchJob {
  id: string;
  url: string;
  status: 'QUEUED' | 'RUNNING' | 'DONE' | 'FAILED';
  document_id: string | null;
  /** True when the bytes matched a version the source already had. */
  unchanged: boolean;
  error: string | null;
  created_at: string;
  finished_at: string | null;
  created_by_name: string;
}

export interface LegalSourceDetail {
  source: LegalSource;
  documents: LegalDocument[];
  jobs: LegalFetchJob[];
}

/** Which document versions are live, as one reviewed set. */
export interface CorpusRelease {
  id: string;
  version_no: number;
  status: 'DRAFT' | 'ACTIVE' | 'ROLLED_BACK';
  note: string;
  created_at: string;
  activated_at: string | null;
  created_by_name: string;
  activated_by_name: string | null;
  documents: string;
  chunks: string;
  /** Chunks still waiting for a vector. Retrieval is lexical-only for these. */
  unembedded: string;
  /** Which model produced the vectors. 'stub' means weak semantic recall. */
  embed_models: string | null;
  /** Standing rules this release hands the writer. '' until somebody writes them. */
  digest: string;
  /** The retrieval check activation is gated on. Null when never run. */
  last_check: {
    status: 'PASSED' | 'FAILED';
    total: number;
    passed: number;
    failed: number;
    results: { query: string; expect: string; passed: boolean }[];
    created_at: string;
  } | null;
}

export interface CorpusSearchResult {
  release: { id: string; versionNo: number };
  tookMs: number;
  results: Array<{
    id: string;
    documentId: string;
    publisher: string;
    title: string;
    headingPath: string;
    text: string;
    canonicalUrl: string | null;
    versionNo: number;
    score: number;
    /** Which half of the hybrid search found it, and where it placed. */
    lexicalRank: number | null;
    denseRank: number | null;
  }>;
}

/** One prompt's versions, plus everything the authoring screen needs about it. */
export interface AdminPromptVersions {
  prompt: {
    id: string;
    key: string;
    name: string;
    description: string;
    taskType: string;
  };
  items: Array<{
    id: string;
    version_no: number;
    status: string;
    body: string;
    /** '' means the release keeps the request block compiled into the server. */
    user_template: string;
    /**
     * The prohibitions this version released. '' means the ones compiled into
     * the build - see migration 094.
     */
    appended: string;
    note: string;
    created_at: string;
    activated_at: string | null;
    created_by_name: string;
    activated_by_name: string | null;
    /**
     * Re-checked on every read, and advisory: the rules move, saved versions do
     * not. These say what is thin, they do not decide what may be used.
     */
    findings: Array<{ level: 'error' | 'warn'; code: string; message: string }>;
  }>;
  /** The instruction shipped in this build, used when nothing is released. */
  fallback: string;
  /** Placeholders the call site supplies. Empty means none are offered. */
  variables: string[];
  defaults: { system: string; userTemplate: string };
  /** Whether a release of this prompt may carry a request block at all. */
  templatable: boolean;
  /** Whether any call site actually runs this prompt. */
  usedByKey: boolean;
  /**
   * The prohibitions this build ships for a picture prompt, or null.
   *
   * Null for every prompt that appends nothing. For the two image prompts it is
   * the floor: appended after the operator's template renders, so no wording in
   * the template can remove it, and restored by clearing the box. JMC may
   * release their own instead (migration 094) - that one lives on the version,
   * as `items[].appended`.
   */
  appended: string | null;
}

export interface AdminPrompt {
  id: string;
  key: string;
  name: string;
  task_type: string;
  description: string;
  active_version_id: string | null;
  active_version_no: number | null;
  activated_at: string | null;
  /** bigint from count(*), so a string. */
  draft_count: string;
  latest_version_no: number | null;
}

export interface AdminUser {
  id: string;
  email: string | null;
  display_name: string;
  platform_role: 'JMC_ADMIN' | null;
  status: string;
  last_login_at: string | null;
  created_at: string;
  clinic_admin_count: number;
  store_count: number;
  clinic_names: string | null;
  /** The person's picture, so a list of people reads as people. */
  avatar_id: string | null;
  /** 'provisioned' until somebody signs in and claims the account. */
  issuer: string;
  /**
   * What kind of user this is, decided by the server from the reach they hold —
   * platform role, then clinic grant, then store grant, then nothing. Derived in
   * one place so the filter, the chip count and the badge cannot disagree.
   */
  role_bucket: 'JMC_ADMIN' | 'CLINIC_ADMIN' | 'STORE_ADMIN' | 'STORE_USER' | 'NONE';
}

export interface AdminUserList extends ListResponse<AdminUser> {
  facets: AdminFacet[];
}

export interface AdminUserDetail {
  id: string;
  email: string | null;
  display_name: string;
  platform_role: 'JMC_ADMIN' | null;
  status: string;
  last_login_at: string | null;
  created_at: string;
  issuer: string;
  /**
   * The person's picture, in the images table like every other one. Taken from
   * the provider at first sign-in when they have one; null until then.
   */
  avatar_id: string | null;
  /** Whether a password is set. The verifier itself never leaves the server. */
  has_password: boolean;
  password_updated_at: string | null;
  /** Throttled after repeated failures. Temporary, never permanent. */
  locked: boolean;
  /** Clinic-wide grants and store grants in one list; store_name null = clinic-wide. */
  memberships: Array<{
    id: string;
    role: string;
    status: string;
    created_at: string;
    clinic_id: string;
    clinic_name: string;
    store_id: string | null;
    store_name: string | null;
  }>;
  liveSessions: number;
}

export interface AdminOverview {
  counts: {
    clinics: number;
    clinics_active: number;
    stores: number;
    stores_active: number;
    users: number;
    users_suspended: number;
    articles_this_month: number;
    images_this_month: number;
    elevations_active: number;
    /** Vendor spend this period, integer micro-USD. Money never floats. */
    ai_micro_cost_used: number;
    /**
     * JMC's own monthly vendor budget, set on /admin/settings. null when
     * nobody has set one, and the tile then shows spend with no percentage —
     * the sum of every clinic's plan allowance was never a budget.
     */
    ai_budget_micro: number | null;
  };
  /**
   * Contracts already over, or over within ten days. Ended first, then
   * soonest — the order an operator works through them in.
   */
  /**
   * Whether the legal corpus is doing anything at all.
   *
   * With no active release the article check still answers 指摘なし, having
   * consulted no law - indistinguishable, from inside a clinic's screen, from a
   * clean review. So the operator's dashboard is where it has to be visible.
   */
  corpus: {
    active_version: number | null;
    active_digest: boolean;
    documents: string;
    chunks: string;
    unembedded: string;
    sources: string;
    stale_sources: string;
    never_fetched: string;
    /** Current versions the ACTIVE release does not contain. */
    unreleased: string;
    drafts: string;
    refetchDays: number;
  };
  expiring: Array<{
    id: string;
    name: string;
    ends_at: string;
    plan_id: string | null;
    subscription_status: string;
    ended: boolean;
    days_left: number;
  }>;
  /** Current-period spend per clinic, biggest first. */
  aiUsage: Array<{
    id: string;
    name: string;
    status: string;
    ai_micro_cost_used: number;
    ai_micro_cost_limit: number | null;
    articles_used: number;
    images_used: number;
    period_end: string;
  }>;
  recentClinics: Array<{
    id: string;
    name: string;
    status: string;
    created_at: string;
    store_count: number;
  }>;
  recentStores: Array<{
    id: string;
    name: string;
    status: string;
    created_at: string;
    clinic_id: string;
    clinic_name: string;
  }>;
  tasks: Array<{ kind: string; label: string; count: number }>;
  activity: Array<{
    id: number;
    action: string;
    outcome: string;
    created_at: string;
    target_type: string | null;
    clinic_id: string | null;
    clinic_name: string | null;
  }>;
}

export interface AdminAuditEntry {
  id: number;
  action: string;
  outcome: string;
  created_at: string;
  target_type: string | null;
  target_id: string | null;
  actor_role: string | null;
  clinic_name: string | null;
  /** The store the event concerns, when known (migration 103). */
  store_id?: string | null;
  store_name?: string | null;
  actor_email: string | null;
}

export interface AdminClinicDetail {
  clinic: {
    id: string;
    name: string;
    status: string;
    timezone: string;
    created_at: string;
    plan_id: string | null;
    plan_name: string | null;
    price_jpy: number | null;
    /** The plan's limits as sold. Empty when the clinic has no subscription. */
    plan_limits: Record<string, number>;
    subscription_status: string | null;
    current_period_start: string | null;
    current_period_end: string | null;
    /**
     * When the agreement finishes, as opposed to when the month does. null is
     * open-ended. Past it, the AI features refuse and nothing else changes.
     */
    ends_at: string | null;
    /** The operating entity behind the brand in `name`. '' when unset. */
    corporate_name: string;
    prefecture: string;
    city: string;
    representative: string;
    address: string;
    phone: string;
    /** 広告管理責任者 - accountable for what the clinic publishes. */
    ad_manager_name: string;
    site_name: string | null;
    site_url: string | null;
    /** The clinic's theme colour, or null for the product's default. */
    brand_color?: string | null;
    default_tone: string | null;
    /** Coalesced by the route: a clinic with no settings row still gets []. */
    default_keywords: string[];
    wordpress_category: string | null;
  };
  stores: Array<{
    id: string;
    name: string;
    status: string;
    created_at: string;
    user_count: number;
  }>;
  /** One row per person, with every store they cover. */
  members: Array<{
    id: string;
    email: string | null;
    display_name: string;
    status: string;
    last_login_at: string | null;
    role: 'CLINIC_ADMIN' | 'STORE_ADMIN' | 'STORE_USER';
    clinic_admin: boolean;
    stores: Array<{ id: string; name: string; admin?: boolean }>;
  }>;
  usage: Array<{
    period_start: string;
    period_end: string;
    articles_used: number;
    images_used: number;
    ai_micro_cost_used: number;
  }>;
  /**
   * The ledger against the rows that exist, per store, this period.
   *
   * JMC-only. A clinic is shown one number - the ledger, because the ledger is
   * what refuses the next article - and this is where a drift between it and
   * reality becomes visible instead of becoming two tiles that disagree.
   */
  reconciliation: Array<{
    store_id: string;
    store_name: string;
    /** Used + reserved in the ledger for the current period. */
    ledger: number;
    /** Articles actually created in the current period. */
    actual: number;
    /** Articles this store has, all time. */
    total: number;
  }>;
  activity: Array<{
    id: number;
    action: string;
    outcome: string;
    created_at: string;
    target_type: string | null;
  }>;
}

export interface AdminStoreDetail {
  store: {
    id: string;
    name: string;
    status: string;
    created_at: string;
    updated_at: string;
    clinic_id: string;
    clinic_name: string;
    clinic_status: string;
    timezone: string;
    plan_id: string | null;
    subscription_status: string | null;
    /** Where the branch is and when it is open. NOT NULL text, so '' is unset. */
    postal_code: string;
    prefecture: string;
    city: string;
    address: string;
    access_note: string;
    phone: string;
    hours_note: string;
    closed_note: string;
    reservation_url: string | null;
    map_url: string | null;
    site_url: string | null;
    default_tone: string | null;
    /** null means this store inherits the clinic's list; [] means it has none. */
    default_keywords: string[] | null;
    wordpress_category: string | null;
    clinic_site_url: string | null;
    clinic_default_tone: string | null;
    clinic_default_keywords: string[];
    clinic_wordpress_category: string | null;
    brand_color?: string | null;
    clinic_brand_color?: string | null;
  };
  members: Array<{
    id: string;
    email: string | null;
    display_name: string;
    status: string;
    last_login_at: string | null;
    role: string;
  }>;
  facts: Array<{ kind: string; approved: number; total: number }>;
  activity: Array<{ id: number; action: string; outcome: string; created_at: string }>;
}

export interface AdminPlan {
  id: string;
  name: string;
  price_jpy: number;
  description: string;
  is_active: boolean;
  updated_at: string;
  /** Absent key means unlimited for that dimension. */
  limits: Record<string, number>;
  /** Live subscriptions on this plan. What makes an edit reviewable. */
  clinics: number;
}

export interface AdminPlanDetail extends AdminPlan {
  clinics_on_plan: Array<{
    id: string;
    name: string;
    status: string;
    subscription_status: string;
    current_period_start: string;
    current_period_end: string;
    store_count: number;
  }>;
}

export interface AdminClinic {
  id: string;
  name: string;
  status: string;
  created_at: string;
  plan_id: string | null;
  subscription_status: string | null;
  current_period_end: string | null;
  /** Contract end. null is open-ended; past it the AI features refuse. */
  ends_at: string | null;
  store_count: number;
  admin_count: number;
  /** Store users, across every store of the clinic. */
  member_count: number;
  articles_used: number;
  images_used: number;
  /** Micro-USD, like everywhere else money appears. */
  ai_micro_cost_used: number;
  articles_limit: number | null;
  ai_micro_cost_limit: number | null;
  /**
   * Last article activity, from the audit trail — the articles table itself is
   * unreadable to a platform session by design (migration 013).
   */
  last_article_at: string | null;
}

/** One filter chip: how many clinics carry this status, or this plan. */
export interface AdminFacet {
  kind: string;
  key: string;
  count: number;
}

export interface AdminClinicList extends ListResponse<AdminClinic> {
  facets: AdminFacet[];
  /** Matching the filters, not on this page — the list is paged. */
  total: number;
}
